import { Component, Inject, OnDestroy } from '@angular/core';
import { HttpStatusCode } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject, merge, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Order } from '@models';
import { SaleOrdersService } from '@services';

// GTM-541: the backend distinguishes 3 business-rule rejections with their
// own status code — surface the real reason for each instead of one generic
// message, so the seller isn't told to check the file when it's actually a
// state conflict (or vice versa). 502 no longer means "already accepted"
// (that used to be the BFF's non-idempotent failure mode) — it's now a
// genuine downstream outage, handled by the generic fallback below.
function businessRejectionMessage(status: number | undefined): string | null {
  switch (status) {
    case HttpStatusCode.BadRequest:
      return 'No se pudo aceptar el registro: no tiene un comprobante adjunto. Adjunta uno para continuar.';
    case HttpStatusCode.Conflict:
      return 'Este registro ya fue aceptado anteriormente.';
    case HttpStatusCode.UnprocessableEntity:
      return 'No se pudo aceptar el registro: el comprobante no es válido (formato o tamaño máximo 10 MB).';
    default:
      return null;
  }
}

@Component({
  standalone: false,
  selector: 'digicem-confirm-receipt-dialog',
  templateUrl: './confirm-receipt-dialog.component.html',
})
export class ConfirmReceiptDialogComponent implements OnDestroy {
  private destroy$ = new Subject<void>();
  private timerCancel$ = new Subject<void>();
  protected order: Order;
  protected onAccept: () => void;
  protected onFailure?: () => void;
  protected hasReceipt: boolean;

  protected selectedFile: File | null = null;
  protected isLoading = false;
  protected serverError = false;
  protected apiError = '';

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      order: Order;
      hasReceipt: boolean;
      onAccept: () => void;
      onFailure?: () => void;
    },
    private dialogRef: MatDialogRef<ConfirmReceiptDialogComponent>,
    private saleOrdersService: SaleOrdersService,
  ) {
    this.order = data.order;
    this.hasReceipt = data.hasReceipt;
    this.onAccept = data.onAccept;
    this.onFailure = data.onFailure;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    // timerCancel$ is separate from destroy$: it cancels only the active
    // error-dismiss timer (e.g. when a new file is selected mid-countdown).
    this.timerCancel$.next();
    this.timerCancel$.complete();
  }

  protected onFileSelected(file: File | null): void {
    this.selectedFile = file;
    if (file) {
      this.serverError = false;
      this.apiError = '';
      this.timerCancel$.next();
    }
  }

  private showApiError(message: string): void {
    this.timerCancel$.next();
    this.apiError = message;
    timer(6000)
      .pipe(takeUntil(merge(this.destroy$, this.timerCancel$)))
      .subscribe(() => (this.apiError = ''));
  }

  protected get hasValidInput(): boolean {
    return this.hasReceipt || !!this.selectedFile;
  }

  protected get canConfirm(): boolean {
    return this.hasValidInput && !this.isLoading;
  }

  protected onCancel(): void {
    this.dialogRef.close();
  }

  protected onConfirm(): void {
    if (!this.canConfirm) return;

    this.isLoading = true;
    this.saleOrdersService
      .patchDeliveredOrder(this.order.id, this.selectedFile ?? undefined)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.onAccept();
          this.dialogRef.close();
        },
        error: (err: { status: number }) => {
          this.isLoading = false;
          const message = businessRejectionMessage(err?.status);
          if (message) {
            this.serverError = true;
            this.showApiError(message);
            this.onFailure?.();
          } else {
            this.showApiError(
              'No se pudo confirmar el registro. Intenta de nuevo.',
            );
          }
        },
      });
  }
}
